import React, { useState } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import Button from "../../../components/ui/Button";
import { parseISO, format, startOfWeek, startOfMonth } from "date-fns";

const SalesChart = ({ data, onDrillDown }) => {
  const [chartType, setChartType] = useState("daily");

  const chartTypeOptions = [
    { value: "daily", label: "Daily", icon: "Calendar" },
    { value: "weekly", label: "Weekly", icon: "CalendarDays" },
    { value: "monthly", label: "Monthly", icon: "CalendarRange" },
  ];

  const groupDataByType = (data, type) => {
    const grouped = {};

    data.forEach((item) => {
      if (!item.date) return; // skip if no date

      const dateObj = parseISO(item.date);

      let key;
      if (type === "weekly") {
        key = format(startOfWeek(dateObj), "yyyy-MM-dd");
      } else if (type === "monthly") {
        key = format(startOfMonth(dateObj), "yyyy-MM");
      } else {
        key = format(dateObj, "yyyy-MM-dd"); // daily
      }

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          sales: 0,
          collection: 0,
          totalRate: 0,
          count: 0,
        };
      }

      grouped[key].sales += item.sales || 0;
      grouped[key].collection += item.collection || 0;
      grouped[key].totalRate += item.collectionRate || 0;
      grouped[key].count += 1;
    });

    return Object.values(grouped).map((g) => ({
      date: g.date,
      sales: g.sales,
      collection: g.collection,
      collectionRate:
        g.count > 0 ? parseFloat((g.totalRate / g.count).toFixed(1)) : 0,
    }));
  };

  const groupedData = groupDataByType(data, chartType);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.dataKey}:</span>
              <span className="font-medium text-foreground">
                {entry.dataKey === "collectionRate"
                  ? `${entry.value}%`
                  : `₹${entry.value.toLocaleString()}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleBarClick = (data) => {
    if (onDrillDown) {
      onDrillDown(data);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-foreground">
            Sales Performance Trends
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Daily sales with collection rate overlay
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:flex-row sm:gap-2">
          {chartTypeOptions.map((option) => (
            <Button
              key={option.value}
              variant={chartType === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => setChartType(option.value)}
              iconName={option.icon}
              iconPosition="left"
              iconSize={14}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="h-[300px] sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={groupedData}
            margin={{ top: 20, right: 0, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis
              yAxisId="left"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `₹${value / 1000}K`}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="sales"
              fill="var(--color-primary)"
              name="Sales"
              radius={[2, 2, 0, 0]}
              onClick={handleBarClick}
              style={{ cursor: "pointer" }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="collectionRate"
              stroke="var(--color-success)"
              strokeWidth={3}
              name="Collection Rate (%)"
              dot={{ fill: "var(--color-success)", strokeWidth: 2, r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SalesChart;
